import { useEffect, useState } from 'react';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import PropTypes from 'prop-types';
import { connectTaxAuthority, restartTaxAuthority } from 'Services/user';
import { useErrors } from 'Components/Organisms/ErrorsProvider/index';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from 'Utils/cn.js';
import Input from 'Components/Atoms/Input/Input';
import Select from 'Components/Atoms/Select/Select';
import RadioGroup from 'Components/Atoms/Radio/RadioGroup';
import Radio from 'Components/Atoms/Radio/Radio';
import Icon from 'Components/Atoms/Icon/Icon';
import { DOCUMENTATION_LINKS, DRAWER_STACK } from 'Utils/constants';
import Textarea from 'Components/Atoms/Textarea/Textarea';
import Badge from 'Components/Atoms/Badge/Badge';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import useDrawerStack from 'Hooks/useDrawerStack';

function TaxAuthorityItemActions({ value, status, busy, onConnect, onRestart }) {
    const { workspace } = useWorkspaceSelected();

    const [taxId, setTaxId] = useState(workspace?.tax_authority_tax_id ?? workspace?.invoicing_address?.tax_id);
    const [taxIdError, setTaxIdError] = useState(null);
    const [province, setProvince] = useState(workspace?.es_tbai_province ?? null);
    const [exemptReason, setExemptReason] = useState(null);
    const [exemptReasonOther, setExemptReasonOther] = useState('');

    const PROVINCE_OPTIONS = [
        { value: 'VI', label: 'Álava' },
        { value: 'BI', label: 'Bizkaia' },
        { value: 'SS', label: 'Gipuzkoa' },
    ];

    const { t } = useTranslation('invoices');

    const EXEMPT_REASON_OPTIONS = [
        { value: 'sii', label: t('tax-authorities-exempt-reason-sii') },
        { value: 'foreign', label: t('tax-authorities-exempt-reason-foreign') },
        { value: 'pais-vasco', label: t('tax-authorities-exempt-reason-pais-vasco') },
        { value: 'navarra', label: t('tax-authorities-exempt-reason-navarra') },
        { value: 'no-application', label: t('tax-authorities-exempt-reason-no-application') },
        { value: 'not-sif', label: t('tax-authorities-exempt-reason-not-sif') },
        { value: 'other', label: t('tax-authorities-exempt-reason-other') },
    ];

    if (status === null) {
        const isExemptValid =
            value !== 'exempt' ||
            (exemptReason !== null && (exemptReason !== 'other' || exemptReasonOther.trim() !== ''));

        return (
            <div className="flex flex-col gap-5 mt-5">
                {value !== 'exempt' && (
                    <>
                        <Input
                            value={taxId}
                            onChange={(ev) => {
                                setTaxId(ev.target.value);
                                if (taxIdError) setTaxIdError(null);
                            }}
                            label={t('tax-authorities-tax-id')}
                        />
                        {value === 'ticketbai' && (
                            <Select
                                options={PROVINCE_OPTIONS}
                                value={province}
                                onChange={setProvince}
                                label={t('tax-authorities-ticketbai-province')}
                                placeholder={t('tax-authorities-ticketbai-province-placeholder')}
                                className="w-full"
                            />
                        )}
                    </>
                )}
                {taxIdError && (
                    <div className="bg-danger/10 rounded-md text-sm flex items-start" role="alert">
                        <div className="flex items-start md:items-center grow gap-3 p-4">
                            <div className="shrink-0">
                                <Icon name="circle-alert" color="currentColor" className="text-danger" />
                            </div>
                            <div className="grow text-danger">{taxIdError}</div>
                        </div>
                    </div>
                )}
                {value === 'exempt' && (
                    <div className="flex flex-col gap-3 mb-5">
                        <p className="text-sm font-semibold text-dark-blue">
                            {t('tax-authorities-exempt-reason-label')}
                        </p>
                        <RadioGroup
                            name="exempt-reason"
                            value={exemptReason}
                            onChange={(ev) => setExemptReason(ev.target.value)}
                            direction="column"
                        >
                            {EXEMPT_REASON_OPTIONS.map((opt) => (
                                <Radio key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Radio>
                            ))}
                        </RadioGroup>
                        {exemptReason === 'other' && (
                            <div className="pt-2">
                                <Textarea
                                    value={exemptReasonOther}
                                    autoFocus
                                    onChange={(ev) => setExemptReasonOther(ev.target.value)}
                                    label={t('tax-authorities-exempt-reason-other-label')}
                                />
                            </div>
                        )}
                    </div>
                )}
                <Button
                    variant="primary"
                    onClick={() => {
                        if (!taxId?.trim()) {
                            setTaxIdError(t('tax-authorities-error-tax-id-required'));
                            return;
                        }
                        onConnect(value, taxId, value === 'ticketbai' ? { province } : undefined);
                    }}
                    isBusy={busy}
                    disabled={!isExemptValid || (value === 'ticketbai' && !province)}
                >
                    {t('tax-authorities-start-connection')}
                </Button>
            </div>
        );
    }

    if (status === 'pending_documentation') {
        return (
            <div className="grid gap-5 mt-5">
                {value === 'ticketbai' && (
                    <Select
                        options={PROVINCE_OPTIONS}
                        value={province}
                        disabled
                        onChange={setProvince}
                        label={t('tax-authorities-ticketbai-province')}
                        placeholder={t('tax-authorities-ticketbai-province-placeholder')}
                        className="w-full"
                    />
                )}

                <div className="flex flex-col gap-2">
                    <Button
                        variant="primary"
                        onClick={() => onConnect(value, taxId, value === 'ticketbai' ? { province } : undefined)}
                        isBusy={busy}
                    >
                        {t('tax-authorities-complete-docs')}
                    </Button>
                    <Button variant="default" onClick={onRestart} isBusy={busy}>
                        {t('tax-authorities-restart-connection')}
                    </Button>
                </div>
            </div>
        );
    }

    if (status === 'processing') {
        return (
            <div className="flex flex-col gap-2 mt-5">
                <strong>{t(`tax-authorities-status-processing`, { taxId: taxId })}</strong>
                <Button variant="default" onClick={onRestart} isBusy={busy}>
                    {t('tax-authorities-restart-connection')}
                </Button>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="flex flex-col gap-2 mt-5">
                <strong>
                    {t(`tax-authorities-status-error`, { message: workspace?.tax_authority_error_message })}
                </strong>
                <Button variant="default" onClick={onRestart} isBusy={busy}>
                    {t('tax-authorities-restart-connection')}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 mt-5">
            <strong>{t(`tax-authorities-status-ok`, { taxId: taxId })}</strong>
            {workspace?.es_tbai_province && (
                <Tooltip label={t('tax-authorities-ticketbai-province-edit-hint')}>
                    <div className="py-2">
                        <Select
                            options={PROVINCE_OPTIONS}
                            value={workspace.es_tbai_province}
                            label={t('tax-authorities-ticketbai-province')}
                            disabled
                            onChange={() => {}}
                            className="w-full"
                        />
                    </div>
                </Tooltip>
            )}
            <Button variant="default" onClick={onRestart} isBusy={busy}>
                {t('tax-authorities-reject-connection')}
            </Button>
        </div>
    );
}

TaxAuthorityItemActions.propTypes = {
    value: PropTypes.string,
    status: PropTypes.oneOf(['pending_documentation', 'processing', 'error', 'ok']),
    busy: PropTypes.bool,
    onConnect: PropTypes.func.isRequired,
    onRestart: PropTypes.func.isRequired,
};

function TaxAuthorityItem({
    name,
    description,
    value,
    valueSelected,
    status,
    disabled,
    busy,
    onChange,
    onConnect,
    onRestart,
    documentLinks,
}) {
    const isSelected = value === valueSelected;

    return (
        <label
            className={cn(
                'inline-flex items-start select-none gap-3 border border-gray-200 shadow-sm px-3 py-4 rounded-lg',
                !isSelected && 'cursor-pointer',
            )}
        >
            <div className="inline-flex size-5 relative shrink-0">
                <input
                    type="radio"
                    name="tax-authority"
                    className={cn(
                        'peer bg-white appearance-none size-full border-1 border-gray-300 rounded-full shadow-sm transition-all cursor-pointer text-dark-blue',
                        'focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary',
                        'disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed',
                    )}
                    onChange={() => onChange(value)}
                    checked={isSelected}
                    disabled={disabled}
                />

                <span className="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2 size-3 rounded-full peer-checked:bg-dark-blue peer-checked:scale-100 scale-0 transition-transform peer-disabled:bg-gray-600" />
            </div>

            <span className="grow pb-1">
                <div className="font-semibold text-dark-blue mb-3 text-base relative">{name}</div>
                <div className="gap-x-4 gap-y-3 text-xs text-gray-500">
                    <div>{description}</div>
                    {isSelected && value !== null && (
                        <div className="grid gap-3">
                            <TaxAuthorityItemActions
                                value={value}
                                status={status}
                                busy={busy}
                                onConnect={onConnect}
                                onRestart={onRestart}
                            />

                            <div className="flex justify-between flex-wrap gap-1">
                                {documentLinks?.map((link) => (
                                    <div key={link.url} className="py-1">
                                        <a
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs underline hover:no-underline inline-flex items-center gap-1"
                                        >
                                            {link.label}

                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    stroke="currentColor"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M15 3h6m0 0v6m0-6L10 14m8-1v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                                                ></path>
                                            </svg>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </span>
        </label>
    );
}

TaxAuthorityItem.propTypes = {
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    description: PropTypes.string.isRequired,
    value: PropTypes.string,
    valueSelected: PropTypes.string,
    status: PropTypes.oneOf(['pending_documentation', 'processing', 'error', 'ok']),
    disabled: PropTypes.bool,
    busy: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onConnect: PropTypes.func.isRequired,
    onRestart: PropTypes.func.isRequired,
    documentLinks: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
    ),
};

export default function InvoicesInvoicingTaxAuthoritiesDrawer({ onClose }) {
    const { workspace } = useWorkspaceSelected();
    const { t, i18n } = useTranslation('invoices');
    const [processing, setProcessing] = useState(false);
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const [taxAuthority, setTaxAuthority] = useState(workspace?.tax_authority);
    const [taxAuthorityStatus, setTaxAuthorityStatus] = useState(workspace?.tax_authority_status);
    const [connectError, setConnectError] = useState(null);
    const { open } = useDrawerStack();

    useEffect(() => {
        setTaxAuthority(workspace?.tax_authority);
        setTaxAuthorityStatus(workspace?.tax_authority_status);
    }, [workspace]);

    const onConnect = async (taxAuthority, taxId, options) => {
        if (processing) {
            return;
        }

        setConnectError(null);
        setProcessing(true);

        try {
            await connectTaxAuthority(taxAuthority, taxId, options);
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.description === 'requires_documentation') {
                window.open(error.response.data.info.documentation_url, '_blank', 'noopener,noreferrer');
                onClose?.();
            } else if (
                error.response?.status === 400 &&
                error.response?.data?.description === 'tax_id_already_registered'
            ) {
                setConnectError(t('tax-authorities-error-tax-id-registered'));
            } else if (error.response?.status === 400 && error.response?.data?.description === 'invalid_tax_id') {
                setConnectError(t('tax-authorities-error-invalid-tax-id'));
            } else if (
                error.response?.status === 400 &&
                error.response?.data?.description === 'missing_supplier_data'
            ) {
                open(DRAWER_STACK.INVOICE_INVOICING_CONFIG, {
                    title: t('billing-information'),
                    entityTypeRequired: true,
                });
            } else {
                showUnknownError(error);
            }
        } finally {
            await queryClient.invalidateQueries({ queryKey: ['project-workspace-selected'] });
            setProcessing(false);
        }
    };

    const onRestart = async () => {
        if (processing) {
            return;
        }

        setProcessing(true);

        try {
            await restartTaxAuthority();
        } catch (error) {
            showUnknownError(error);
        } finally {
            await queryClient.invalidateQueries({ queryKey: ['project-workspace-selected'] });
            setProcessing(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <p className="text-gray-500">{t('tax-authorities-note')}</p>
            <a
                href={
                    DOCUMENTATION_LINKS.VERIFACTU_OVERVIEW[i18n.language] || DOCUMENTATION_LINKS.VERIFACTU_OVERVIEW.EN
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline hover:no-underline inline-flex items-center gap-1 mt-2"
            >
                {t('tax-authorities-learn-more')}
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24">
                    <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 3h6m0 0v6m0-6L10 14m8-1v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                    ></path>
                </svg>
            </a>

            {connectError && (
                <div className="bg-danger/10 rounded-md mb-8 text-sm flex items-start mt-4" role="alert">
                    <div className="flex items-start md:items-center grow gap-3 p-4">
                        <div className="shrink-0">
                            <Icon name="circle-alert" color="currentColor" className="text-danger" />
                        </div>
                        <div className="grow text-danger">{connectError}</div>
                    </div>
                </div>
            )}

            <fieldset className="flex flex-col gap-4 mt-8">
                <TaxAuthorityItem
                    name={t('tax-authorities-none')}
                    description={t('tax-authorities-none-description')}
                    value={null}
                    valueSelected={taxAuthority}
                    status={taxAuthorityStatus}
                    disabled={workspace?.tax_authority !== null}
                    busy={processing}
                    onChange={setTaxAuthority}
                    onConnect={onConnect}
                    onRestart={onRestart}
                />
                <TaxAuthorityItem
                    name={
                        <div className="flex gap-2 items-center">
                            {t('tax-authorities-verifactu')}
                            <Badge>{t('tax-authorities-recommended')}</Badge>
                        </div>
                    }
                    description={t('tax-authorities-verifactu-description')}
                    value="verifactu"
                    valueSelected={taxAuthority}
                    status={taxAuthorityStatus}
                    disabled={workspace?.tax_authority !== null}
                    busy={processing}
                    onChange={setTaxAuthority}
                    onConnect={onConnect}
                    onRestart={onRestart}
                    documentLinks={[
                        {
                            label: t('tax-authorities-verifactu-docs-link'),
                            url:
                                DOCUMENTATION_LINKS.VERIFACTU_DOCS[i18n.language] ||
                                DOCUMENTATION_LINKS.VERIFACTU_DOCS.EN,
                        },
                        {
                            label: t('tax-authorities-verifactu-declaration-link'),
                            url:
                                DOCUMENTATION_LINKS.VERIFACTU_DECLARATION[i18n.language] ||
                                DOCUMENTATION_LINKS.VERIFACTU_DECLARATION.EN,
                        },
                    ]}
                />
                <TaxAuthorityItem
                    name={t('tax-authorities-exempt')}
                    description={t('tax-authorities-exempt-description')}
                    value="exempt"
                    valueSelected={taxAuthority}
                    status={taxAuthorityStatus}
                    disabled={workspace?.tax_authority !== null}
                    busy={processing}
                    onChange={setTaxAuthority}
                    onConnect={onConnect}
                    onRestart={onRestart}
                />

                <TaxAuthorityItem
                    name={t('tax-authorities-ticketbai')}
                    description={t('tax-authorities-ticketbai-description')}
                    value="ticketbai"
                    valueSelected={taxAuthority}
                    status={taxAuthorityStatus}
                    disabled={workspace?.tax_authority !== null}
                    busy={processing}
                    onChange={setTaxAuthority}
                    onConnect={onConnect}
                    onRestart={onRestart}
                />
            </fieldset>
        </div>
    );
}

InvoicesInvoicingTaxAuthoritiesDrawer.propTypes = {
    onClose: PropTypes.func,
};
